import axios from "axios";
import { getApiUrlWithVersion } from "./environmentService";
import {
  CloseScheduledEdition,
  NewsletterEdition,
  NewsletterEditionChangeRequest,
  NewsletterEditionLink,
  NewsletterEditionStatus,
  PostNewsletterEditionInput,
  PutNewsletterEditionInput,
} from "../types/newsletterEdition";
import { getAuthUser } from "./authService";

/**
 * This component is the integration point between the frontend and the backend for campaigns and newsletter editions.
 */
class CampaignService {
  // TODO: take in a campaignId to manage all actions for that campaign
  constructor() { }

  private getAuthHeaders(): object {
    return {
      Authorization: getAuthUser()?.accessToken,
    };
  }

  async getNewsletterEdition(
    newsletterEditionId: string,
  ): Promise<NewsletterEdition> {
    const response = await axios.get(
      `${getApiUrlWithVersion()}/newsletter_edition/${newsletterEditionId}`,
      { headers: this.getAuthHeaders() },
    );
    const parsedData = response.data;
    // parsedData.email_template = JSON.parse(parsedData.email_template);
    parsedData.id = newsletterEditionId;
    // parsedData.email_template = JSON.parse(parsedData.email_template);
    return parsedData;
  }

  async getNewsletterEditionTemplate(
    newsletterEditionId: string,
  ): Promise<object> {
    const newsletterEdition: NewsletterEdition =
      await this.getNewsletterEdition(newsletterEditionId);
    return newsletterEdition.email_template;
  }

  /**
   * List all newsletter editions for the current user.
   */
  async listNewsletterEditions(): Promise<NewsletterEdition[]> {
    const response = await axios.get(
      `${getApiUrlWithVersion()}/newsletter_edition`,
      { headers: this.getAuthHeaders() },
    );
    return response.data;
  }

  async renderEditionHtml(newsletterEditionId: string): Promise<string> {
    const response = await axios.post(
      `${getApiUrlWithVersion()}/newsletter_edition/${newsletterEditionId}/render_html`,
      {},
      { headers: this.getAuthHeaders() },
    );
    return response.data.html_content;
  }

  /**
   * Create a new newsletter edition.
   *
   * Returns the id of the created newsletter edition.
   */
  async createNewsletterEdition(
    postNewsletterEditionInput: PostNewsletterEditionInput,
  ): Promise<string> {
    const response = await axios.post(
      `${getApiUrlWithVersion()}/newsletter_edition`,
      postNewsletterEditionInput,
      { headers: this.getAuthHeaders() },
    );
    return response.data.id;
  }

  /**
   * Update a newsletter edition.
   */
  async updateNewsletterEdition(
    newsletterEditionId: string,
    newsletterEdition: PutNewsletterEditionInput,
  ): Promise<NewsletterEdition> {
    const response = await axios.put(
      `${getApiUrlWithVersion()}/newsletter_edition/${newsletterEditionId}`,
      newsletterEdition,
      { headers: this.getAuthHeaders() },
    );
    return response.data;
  }

  /**
   * Update a newsletter edition's status.
   */
  async updateNewsletterEditionStatus(
    newsletterEditionId: string,
    status: NewsletterEditionStatus,
  ): Promise<NewsletterEdition> {
    const response = await axios.patch(
      `${getApiUrlWithVersion()}/newsletter_edition/${newsletterEditionId}/status`,
      { status },
      { headers: this.getAuthHeaders() },
    );
    return response.data;
  }

  /**
   * Update a newsletter edition's scheduled send start date.
   */
  async updateNewsletterEditionScheduledSendDate(
    newsletterEditionId: string,
    scheduled_send_start_date: string,
  ): Promise<NewsletterEdition> {
    const response = await axios.patch(
      `${getApiUrlWithVersion()}/newsletter_edition/${newsletterEditionId}/schedule`,
      { scheduled_send_start_date },
      { headers: this.getAuthHeaders() },
    );
    return response.data;
  }

  async rateNewsletterEdition(newsletterEditionId: string, rating: number): Promise<void> {
    await axios.patch(
      `${getApiUrlWithVersion()}/newsletter_edition/${newsletterEditionId}/rating`,
      { rating },
      { headers: this.getAuthHeaders() },
    );
  }

  /**
   * Delete a newsletter edition.
   *
   * Returns an error on failure, and 204 No Content on success.
   */
  async deleteNewsletterEdition(newsletterEditionId: string): Promise<any> {
    const response = await axios.delete(
      `${getApiUrlWithVersion()}/newsletter_edition/${newsletterEditionId}`,
      { headers: this.getAuthHeaders() },
    );
    return response.data;
  }

  /**
   * Update a newsletter edition's links.
   */
  async updateNewsletterEditionLinks(
    newsletterEditionId: string,
    newsletterEditionLinks: NewsletterEditionLink[],
  ): Promise<NewsletterEdition> {
    const response = await axios.put(
      `${getApiUrlWithVersion()}/newsletter_edition/${newsletterEditionId}/links`,
      { links: newsletterEditionLinks },
      { headers: this.getAuthHeaders() },
    );
    return response.data;
  }

  async submitNewsletterEditionFeedback(
    newsletterEditionId: string,
    comment: string,
    makeChanges: boolean = true,
  ): Promise<NewsletterEditionChangeRequest> {
    const response = await axios.post(
      `${getApiUrlWithVersion()}/newsletter_edition/${newsletterEditionId}/comment`,
      { comment, make_changes: makeChanges },
      { headers: this.getAuthHeaders() },
    );

    return response.data;
  }

  async submitNewsletterEditionCustomContent(
    newsletterEditionId: string,
    text: string,
  ): Promise<any> {
    const response = await axios.post(
      `${getApiUrlWithVersion()}/newsletter_edition/${newsletterEditionId}/custom_content`,
      { text },
      { headers: this.getAuthHeaders() },
    );

    return response.data
  }

  async sendTestNewsletterEditionEmail(
    newsletterEditionId: string,
    to: string,
    html: string,
    subject: string,
    preheader?: string,
  ): Promise<any> {
    const response = await axios.post(
      `${getApiUrlWithVersion()}/newsletter_edition/${newsletterEditionId}/send_test`,
      { to, html, subject, preheader },
      { headers: this.getAuthHeaders() },
    );

    return response.data;
  }

  async addIngredientToNewsletterEdition(
    newsletterEditionId: string,
    ingredient_id: string,
    instructions?: string,
    ingredient_format?: string,
    model?: 'balanced' | 'smart',
    provider?: 'anthropic' | 'openai',
    use_selective_placeholder_merge?: boolean,
  ): Promise<any> {
    // Start the async job
    const startResponse = await axios.post(
      `${getApiUrlWithVersion()}/newsletter_edition/${newsletterEditionId}/add_ingredient_async`,
      {
        ingredient_id,
        instructions,
        ingredient_format,
        model,
        provider,
        use_selective_placeholder_merge,
      },
      { headers: this.getAuthHeaders() },
    );

    const jobId = startResponse.data.job_id;

    // Poll for completion (max 2 minutes)
    const maxAttempts = 40;
    const pollInterval = 3000; // 3 seconds

    for (let attempt = 0; attempt < maxAttempts; attempt++) {
      // Wait before polling (except on first attempt)
      if (attempt > 0) {
        await new Promise(resolve => setTimeout(resolve, pollInterval));
      }

      try {
        const statusResponse = await axios.get(
          `${getApiUrlWithVersion()}/newsletter_edition/add_ingredient_status/${jobId}`,
          { headers: this.getAuthHeaders() },
        );

        const status = statusResponse.data;
        console.log(`Poll attempt ${attempt + 1}: Job ${jobId} status:`, status);

        if (status.status === 'completed') {
          return { success: true, message: status.message };
        } else if (status.status === 'failed') {
          throw new Error(status.error || 'Ingredient creation failed');
        }
        // If still processing/queued, continue polling
      } catch (error: any) {
        console.error(`Poll attempt ${attempt + 1} failed:`, error);
        if (error.response?.status === 404 && attempt < 5) {
          // For the first few attempts, 404 might mean the job hasn't started yet
          console.log('Job not found yet, continuing to poll...');
          continue;
        }
        throw error;
      }
    }

    // If we've exhausted all attempts, throw timeout error
    throw new Error('Ingredient creation timed out after 60 seconds');
  }

  async getNewsletterEditionIngredients(
    newsletterEditionId: string,
  ): Promise<any> {
    const response = await axios.get(
      `${getApiUrlWithVersion()}/newsletter_edition/${newsletterEditionId}/ingredients`,
      { headers: this.getAuthHeaders() },
    );

    return response.data;
  }

  async getNewsletterSources(): Promise<NewsletterEditionLink[]> {
    const response = await axios.get(
      `${getApiUrlWithVersion()}/newsletter_sources`,
      { headers: this.getAuthHeaders() },
    );

    return response.data;
  }

  async updateNewsletterSources(newsletterSources: NewsletterEditionLink[]): Promise<NewsletterEditionLink[]> {
    const response = await axios.put(
      `${getApiUrlWithVersion()}/newsletter_sources`,
      { sources: newsletterSources },
      { headers: this.getAuthHeaders() },
    );

    return response.data;
  }

  /**
   * Get a BeeFree plugin session token.
   */
  async getBeeFreeToken(): Promise<string> {
    const response = await axios.get(
      `${getApiUrlWithVersion()}/template_editor/session_key`,
      { headers: this.getAuthHeaders() },
    );
    return response.data.access_token;
  }

  /**
   * Get default template override from user's organization blueprints.
   */
  async getDefaultTemplateOverride(): Promise<{ email_template: any, blueprint_id: string | null }> {
    const response = await axios.get(
      `${getApiUrlWithVersion()}/newsletter_edition/default_template`,
      { headers: this.getAuthHeaders() },
    );
    return response.data;
  }

  /**
   * Find another edition that is already approved (or mid-send) and scheduled to
   * go out within 5 days of the edition being approved. Past-due send dates are
   * treated as "now" because the send pipeline sends past-due approved editions
   * immediately. Returns the closest such edition, or null.
   */
  findCloseScheduledEdition = (
    editions: NewsletterEdition[],
    currentEdition: NewsletterEdition,
  ): CloseScheduledEdition | null => {
    const DAY_MS = 24 * 60 * 60 * 1000;
    const now = Date.now();

    const effectiveSendTime = (date: string | null | undefined): number | null => {
      if (!date) return null;
      const time = new Date(date).getTime();
      if (isNaN(time)) return null;
      return Math.max(time, now);
    };

    const currentTime =
      effectiveSendTime(currentEdition.scheduled_send_start_date) ?? now;

    let closest: { edition: NewsletterEdition; diffDays: number } | null = null;
    for (const edition of editions) {
      if (String(edition.id) === String(currentEdition.id)) continue;
      if (
        edition.status !== NewsletterEditionStatus.Approved &&
        edition.status !== NewsletterEditionStatus.Sending
      ) continue;
      if (edition.is_welcome_email) continue;

      const editionTime = effectiveSendTime(edition.scheduled_send_start_date);
      if (editionTime === null) continue;

      const diffDays = Math.abs(editionTime - currentTime) / DAY_MS;
      if (diffDays > 5) continue;
      if (!closest || diffDays < closest.diffDays) {
        closest = { edition, diffDays };
      }
    }

    if (!closest) return null;
    return {
      title: closest.edition.title,
      scheduledSendDate: closest.edition.scheduled_send_start_date,
      daysApart: Math.round(closest.diffDays),
    };
  }

  getToggledStatus = (status: NewsletterEditionStatus): NewsletterEditionStatus | null => {
    switch (status) {
      case NewsletterEditionStatus.Approved:
        return NewsletterEditionStatus.AwaitingApproval;
      case NewsletterEditionStatus.Preparing:
        return NewsletterEditionStatus.Approved;
      case NewsletterEditionStatus.AwaitingApproval:
        return NewsletterEditionStatus.Approved;
      case NewsletterEditionStatus.Expired:
        return NewsletterEditionStatus.Approved;
      default: {
        return null;
      }
    }
  }

  async getSavedIngredientPrompts(): Promise<any> {
    const response = await axios.get(
      `${getApiUrlWithVersion()}/saved_ingredient_prompts`,
      { headers: this.getAuthHeaders() }
    );
    return response.data;
  }

  async createSavedIngredientPrompt(name: string, content: string, category?: string): Promise<any> {
    const response = await axios.post(
      `${getApiUrlWithVersion()}/saved_ingredient_prompts`,
      { name, content, category },
      { headers: this.getAuthHeaders() }
    );
    return response.data;
  }

  async updateSavedIngredientPrompt(id: string, data: { name?: string; content?: string; category?: string }): Promise<any> {
    const response = await axios.put(
      `${getApiUrlWithVersion()}/saved_ingredient_prompts/${id}`,
      data,
      { headers: this.getAuthHeaders() }
    );
    return response.data;
  }

  async deleteSavedIngredientPrompt(id: string, permanent: boolean = false): Promise<any> {
    const response = await axios.delete(
      `${getApiUrlWithVersion()}/saved_ingredient_prompts/${id}${permanent ? '?permanent=true' : ''}`,
      { headers: this.getAuthHeaders() }
    );
    return response.data;
  }

  async trackSavedIngredientPromptUsage(id: string): Promise<any> {
    const response = await axios.post(
      `${getApiUrlWithVersion()}/saved_ingredient_prompts/${id}/track_usage`,
      {},
      { headers: this.getAuthHeaders() }
    );
    return response.data;
  }

  async getNewsletterEditionBlueprint(newsletterEditionId: string): Promise<any> {
    const response = await axios.get(
      `${getApiUrlWithVersion()}/newsletter_edition/${newsletterEditionId}/blueprint`,
      { headers: this.getAuthHeaders() }
    );
    return response.data;
  }
  //TODO (seif) : remove this , it's not being used
  async fastCreateIngredients(newsletterEditionId: string): Promise<any> {
    const response = await axios.post(
      `${getApiUrlWithVersion()}/newsletter_edition/${newsletterEditionId}/fast_create_ingredients`,
      {},
      { headers: this.getAuthHeaders() }
    );
    return response.data;
  }

  async pollFastCreateStatus(jobId: string): Promise<any> {
    const response = await axios.get(
      `${getApiUrlWithVersion()}/newsletter_edition/fast_create_status/${jobId}`,
      { headers: this.getAuthHeaders() }
    );
    return response.data;
  }

  async generateSubjectLines(newsletterEditionId: string, additionalInstructions?: string): Promise<{ subject_lines: string[] }> {
    const response = await axios.post(
      `${getApiUrlWithVersion()}/newsletter_edition/${newsletterEditionId}/generate_subject_lines`,
      { additional_instructions: additionalInstructions },
      { headers: this.getAuthHeaders() }
    );
    return response.data;
  }

  async generateNewsletterIntro(newsletterEditionId: string, additionalInstructions?: string): Promise<{ intro: string }> {
    const response = await axios.post(
      `${getApiUrlWithVersion()}/newsletter_edition/${newsletterEditionId}/generate_intro`,
      { additional_instructions: additionalInstructions },
      { headers: this.getAuthHeaders() }
    );
    return response.data;
  }

  async backupEmailTemplate(newsletterEditionId: string): Promise<{ message: string; backup_created_at?: string }> {
    const response = await axios.post(
      `${getApiUrlWithVersion()}/newsletter_edition/${newsletterEditionId}/backup_template`,
      {},
      { headers: this.getAuthHeaders() }
    );
    return response.data;
  }

  async restoreEmailTemplate(newsletterEditionId: string): Promise<{ message: string }> {
    const response = await axios.post(
      `${getApiUrlWithVersion()}/newsletter_edition/${newsletterEditionId}/restore_template`,
      {},
      { headers: this.getAuthHeaders() }
    );
    return response.data;
  }

  async copyNewsletterEdition(newsletterEditionId: string): Promise<{ id: string; message: string }> {
    const response = await axios.post(
      `${getApiUrlWithVersion()}/newsletter_edition/${newsletterEditionId}/copy`,
      {},
      { headers: this.getAuthHeaders() }
    );
    return response.data;
  }

  async aiCopyEdit(newsletterEditionId: string, prompt: string): Promise<{ message: string }> {
    const response = await axios.post(
      `${getApiUrlWithVersion()}/newsletter_edition/${newsletterEditionId}/ai_copy_edit`,
      { prompt },
      { headers: this.getAuthHeaders() }
    );
    return response.data;
  }

  async evaluateContent(newsletterEditionId: string): Promise<{
    pattern_issues: any[];
    pattern_summary: string;
    ai_issues: any[];
    ai_summary: string;
    error?: string;
  }> {
    const response = await axios.post(
      `${getApiUrlWithVersion()}/newsletter_edition/${newsletterEditionId}/evaluate_content`,
      {},
      { headers: this.getAuthHeaders() }
    );
    return response.data;
  }

  async getReviewTodos(newsletterEditionId: string): Promise<{ todos: import('../types/newsletterEdition').ReviewTodo[] }> {
    const response = await axios.get(
      `${getApiUrlWithVersion()}/newsletter_edition/${newsletterEditionId}/todos`,
      { headers: this.getAuthHeaders() }
    );
    return response.data;
  }

  async reviewPipeline(newsletterEditionId: string): Promise<{ todos: import('../types/newsletterEdition').ReviewTodo[] }> {
    const response = await axios.post(
      `${getApiUrlWithVersion()}/newsletter_edition/${newsletterEditionId}/review_pipeline`,
      {},
      { headers: this.getAuthHeaders() }
    );
    return response.data;
  }

  // Re-run the checks at send time. Dry run (apply=false) returns `pending`
  // without touching the saved checklist; apply=true overrides the checklist
  // with the fresh findings and returns `todos`.
  async reviewRecheck(
    newsletterEditionId: string,
    apply = false
  ): Promise<{ pending?: import('../types/newsletterEdition').ReviewTodo[]; todos?: import('../types/newsletterEdition').ReviewTodo[] }> {
    const response = await axios.post(
      `${getApiUrlWithVersion()}/newsletter_edition/${newsletterEditionId}/review_recheck`,
      { apply },
      { headers: this.getAuthHeaders() }
    );
    return response.data;
  }

  async updateReviewTodo(newsletterEditionId: string, todoId: string, done: boolean): Promise<{ todos: import('../types/newsletterEdition').ReviewTodo[] }> {
    const response = await axios.patch(
      `${getApiUrlWithVersion()}/newsletter_edition/${newsletterEditionId}/todos/${todoId}`,
      { done },
      { headers: this.getAuthHeaders() }
    );
    return response.data;
  }

  async bulkUpdateReviewTodos(newsletterEditionId: string, checkId: string, done: boolean): Promise<{ todos: import('../types/newsletterEdition').ReviewTodo[] }> {
    const response = await axios.patch(
      `${getApiUrlWithVersion()}/newsletter_edition/${newsletterEditionId}/todos`,
      { check_id: checkId, done },
      { headers: this.getAuthHeaders() }
    );
    return response.data;
  }

  async autoResolveTodo(newsletterEditionId: string, todoId: string): Promise<{ todos: import('../types/newsletterEdition').ReviewTodo[] }> {
    const response = await axios.post(
      `${getApiUrlWithVersion()}/newsletter_edition/${newsletterEditionId}/todos/${todoId}/resolve`,
      {},
      { headers: this.getAuthHeaders() }
    );
    return response.data;
  }

  async sendForReview(newsletterEditionId: string): Promise<{ message: string }> {
    const response = await axios.post(
      `${getApiUrlWithVersion()}/newsletter_edition/${newsletterEditionId}/send_for_review`,
      {},
      { headers: this.getAuthHeaders() }
    );
    return response.data;
  }

  async getAvailableIngredientFormats(blueprintId?: string | null): Promise<{
    formats: Array<{
      name: string;
      type: 'base' | 'custom';
      description: string;
      template_id?: number;
      ingredient_type?: string;
    }>;
    base_formats: Array<{ name: string; type: string; description: string }>;
    custom_formats: Array<{
      name: string;
      type: string;
      description: string;
      template_id: number;
      ingredient_type: string;
    }>;
  }> {
    const url = blueprintId
      ? `${getApiUrlWithVersion()}/onboarding/blueprints/${blueprintId}/available_formats`
      : `${getApiUrlWithVersion()}/onboarding/blueprints/available_formats`;
    const response = await axios.get(url, { headers: this.getAuthHeaders() });
    return response.data;
  }

  async getFrequentlyUsedInstructions(): Promise<{
    frequently_used: Array<{
      instructions: string;
      usage_count: number;
    }>;
  }> {
    const response = await axios.get(
      `${getApiUrlWithVersion()}/saved_ingredient_prompts/frequently_used`,
      { headers: this.getAuthHeaders() }
    );
    return response.data;
  }

  async logBeeFreeError(
    errorType: 'ERROR' | 'WARNING',
    errorObject: object,
    newsletterEditionId?: string,
    lastIngredientContext?: {
      ingredientIds: string;
      instructions?: string;
      ingredientFormat?: string;
    },
  ): Promise<void> {
    try {
      await axios.post(
        `${getApiUrlWithVersion()}/newsletter_edition/log_beefree_error`,
        {
          error_type: errorType,
          error_object: errorObject,
          newsletter_edition_id: newsletterEditionId,
          last_ingredient_context: lastIngredientContext,
        },
        { headers: this.getAuthHeaders() }
      );
    } catch (error) {
      // Don't throw - logging errors shouldn't break the user experience
      console.error("Failed to log BeeFree error to Slack:", error);
    }
  }
}

const campaignService = new CampaignService();
export default campaignService;
